/**
 * Utility functions for tracking number validation and auto-detection
 */

export const COURIER_COMPANIES = {
  JNT: 'J&T',
  FLASH: 'Flash',
  KERRY: 'Kerry',
  DHL: 'DHL',
  FEDEX: 'FedEx',
  NIM: 'Nim Express',
  THAILAND_POST: 'ไปรษณีย์ไทย'
};

/**
 * Validation rules for each courier company
 */
const VALIDATION_RULES = {
  [COURIER_COMPANIES.JNT]: {
    patterns: [
      /^JTE\d{10,12}$/i, // JTE + 10-12 digits
      /^\d{10,12}$/ // 10-12 digits only
    ],
    description: 'J&T Express: JTE + ตัวเลข 10-12 หลัก หรือตัวเลข 10-12 หลัก'
  },
  [COURIER_COMPANIES.FLASH]: {
    patterns: [
      /^FLASH\d{10,12}$/i, // FLASH + 10-12 digits
      /^\d{10,12}$/ // 10-12 digits only
    ],
    description: 'Flash Express: FLASH + ตัวเลข 10-12 หลัก หรือตัวเลข 10-12 หลัก'
  },
  [COURIER_COMPANIES.KERRY]: {
    patterns: [
      /^KERRY\d{10,12}$/i, // KERRY + 10-12 digits
      /^\d{10,12}$/ // 10-12 digits only
    ],
    description: 'Kerry Express: KERRY + ตัวเลข 10-12 หลัก หรือตัวเลข 10-12 หลัก'
  },
  [COURIER_COMPANIES.DHL]: {
    patterns: [
      /^DHL\d{10,11}$/i, // DHL + 10-11 digits
      /^\d{10,11}$/ // 10-11 digits only
    ],
    description: 'DHL: DHL + ตัวเลข 10-11 หลัก หรือตัวเลข 10-11 หลัก'
  },
  [COURIER_COMPANIES.FEDEX]: {
    patterns: [
      /^FX\d{12}$/i, // FX + 12 digits
      /^\d{12}$/ // 12 digits only
    ],
    description: 'FedEx: FX + ตัวเลข 12 หลัก หรือตัวเลข 12 หลัก'
  },
  [COURIER_COMPANIES.NIM]: {
    patterns: [
      /^\d{10,12}$/ // 10-12 digits only
    ],
    description: 'Nim Express: ตัวเลข 10-12 หลัก'
  }
};

/**
 * Auto-detect courier company from tracking number
 * @param {string} trackingNumber - Tracking number to detect
 * @returns {string|null} - Detected courier company or null
 */
export const detectCourierCompany = (trackingNumber) => {
  if (!trackingNumber || typeof trackingNumber !== 'string') {
    return null;
  }

  const cleaned = trackingNumber.trim().toUpperCase();

  // Check each company's patterns
  for (const [company, rules] of Object.entries(VALIDATION_RULES)) {
    for (const pattern of rules.patterns) {
      if (pattern.test(cleaned)) {
        // Additional checks for ambiguous cases
        if (cleaned.startsWith('JTE')) return COURIER_COMPANIES.JNT;
        if (cleaned.startsWith('FLASH')) return COURIER_COMPANIES.FLASH;
        if (cleaned.startsWith('KERRY')) return COURIER_COMPANIES.KERRY;
        if (cleaned.startsWith('DHL')) return COURIER_COMPANIES.DHL;
        if (cleaned.startsWith('FX')) return COURIER_COMPANIES.FEDEX;
        
        // For numbers without prefix, check length
        if (/^\d+$/.test(cleaned)) {
          const length = cleaned.length;
          if (length === 12) {
            // Could be FedEx or others
            return COURIER_COMPANIES.FEDEX; // Default to FedEx for 12 digits
          } else if (length === 10 || length === 11) {
            // Could be DHL or others
            return COURIER_COMPANIES.DHL; // Default to DHL for 10-11 digits
          } else if (length >= 10 && length <= 12) {
            // Could be J&T, Flash, Kerry, or Nim
            // Return null to let user choose
            return null;
          }
        }
      }
    }
  }

  return null;
};

/**
 * Validate tracking number for a specific courier company
 * @param {string} trackingNumber - Tracking number to validate
 * @param {string} courierCompany - Courier company name
 * @returns {object} - { isValid: boolean, error: string|null }
 */
export const validateTrackingNumber = (trackingNumber, courierCompany) => {
  if (!trackingNumber || typeof trackingNumber !== 'string') {
    return {
      isValid: false,
      error: 'กรุณากรอกเลขพัสดุ'
    };
  }

  const cleaned = trackingNumber.trim();
  
  if (cleaned.length === 0) {
    return {
      isValid: false,
      error: 'กรุณากรอกเลขพัสดุ'
    };
  }

  if (!courierCompany) {
    return {
      isValid: false,
      error: 'กรุณาเลือกบริษัทขนส่ง'
    };
  }

  const rules = VALIDATION_RULES[courierCompany];
  if (!rules) {
    return {
      isValid: false,
      error: 'ไม่พบข้อมูลบริษัทขนส่งที่เลือก'
    };
  }

  // Check if tracking number matches any pattern
  const isValid = rules.patterns.some(pattern => pattern.test(cleaned));
  
  if (!isValid) {
    return {
      isValid: false,
      error: rules.description
    };
  }

  return {
    isValid: true,
    error: null
  };
};

/**
 * Get tracking URL for a courier company
 * @param {string} trackingNumber - Tracking number
 * @param {string} courierCompany - Courier company name
 * @returns {string|null} - Tracking URL or null
 */
export const getTrackingUrl = (trackingNumber, courierCompany) => {
  if (!trackingNumber || !courierCompany) {
    return null;
  }

  const cleaned = trackingNumber.trim();

  switch (courierCompany) {
    case COURIER_COMPANIES.JNT:
      return `https://www.jtexpress.co.th/tracking?trackingNumber=${cleaned}`;
    
    case COURIER_COMPANIES.FLASH:
      return `https://www.flashexpress.com/tracking?trackingNumber=${cleaned}`;
    
    case COURIER_COMPANIES.KERRY:
      return `https://th.kerryexpress.com/en/track/?track=${cleaned}`;
    
    case COURIER_COMPANIES.DHL:
      return `https://www.dhl.com/en/express/tracking.html?AWB=${cleaned}`;
    
    case COURIER_COMPANIES.FEDEX:
      return `https://www.fedex.com/fedextrack/?trknbr=${cleaned}`;
    
    case COURIER_COMPANIES.NIM:
      return `https://www.nimexpress.com/tracking?trackingNumber=${cleaned}`;
    
    default:
      return null;
  }
};

/**
 * Get all available courier companies
 * @returns {Array} - Array of courier company names
 */
export const getAvailableCourierCompanies = () => {
  return Object.values(COURIER_COMPANIES);
};

/**
 * Get validation description for a courier company
 * @param {string} courierCompany - Courier company name
 * @returns {string} - Description
 */
export const getValidationDescription = (courierCompany) => {
  if (!courierCompany) return '';
  const rules = VALIDATION_RULES[courierCompany];
  return rules ? rules.description : '';
};

